import { Button, Modal, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cxiReportGrants,
  type CxiGrantOptions,
  type CxiGrantPracticeArea,
  type CxiGrantUser,
  type CxiReportGrant,
} from "../../../api/cxiReportGrants.service.js";
import {
  SearchableSelect,
  SearchableMultiSelect,
  type SelectOption,
} from "../../../components/Form/SearchableSelect/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import PermissionGate from "../../../components/Permissions/PermissionGate.js";
import styles from "../UserManagement.module.css";

type CxiReportSectionProps = {
  orgId: string;
};

type EditGrantModalProps = {
  orgId: string;
  existingGrant: CxiReportGrant | null;
  users: CxiGrantUser[];
  practiceAreas: CxiGrantPracticeArea[];
  existingUserIds: Set<string>;
  onSaved: () => Promise<void>;
  onClose: () => void;
  onToast: (message: string, type: ToastType) => void;
};

function buildUserOptions(
  users: CxiGrantUser[],
  existingUserIds: Set<string>,
  editingUserId: string | null,
): SelectOption[] {
  return users
    .filter(user => !existingUserIds.has(user.id) || user.id === editingUserId)
    .map(user => ({ value: user.id, label: user.email }));
}

function buildPracticeAreaOptions(practiceAreas: CxiGrantPracticeArea[]): SelectOption[] {
  return practiceAreas.map(pa => ({
    value: pa.id,
    label: pa.country ? `${pa.name} — ${pa.country} · #${pa.id}` : `${pa.name} · #${pa.id}`,
  }));
}

function EditGrantModal({
  orgId,
  existingGrant,
  users,
  practiceAreas,
  existingUserIds,
  onSaved,
  onClose,
  onToast,
}: EditGrantModalProps) {
  const [selectedUserId, setSelectedUserId] = useState<string | number | null>(
    existingGrant?.userId ?? null,
  );
  const [selectedPracticeAreaIds, setSelectedPracticeAreaIds] = useState<number[]>(
    existingGrant?.practiceAreaIds ?? [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const isEditing = existingGrant !== null;

  const userOptions = useMemo(
    () => buildUserOptions(users, existingUserIds, existingGrant?.userId ?? null),
    [users, existingUserIds, existingGrant],
  );

  const practiceAreaOptions = useMemo(
    () => buildPracticeAreaOptions(practiceAreas),
    [practiceAreas],
  );

  async function handleSave() {
    if (!selectedUserId) {
      setFormError("Select a user.");
      return;
    }
    if (selectedPracticeAreaIds.length === 0) {
      setFormError("Select at least one practice area.");
      return;
    }

    setFormError(null);
    setSubmitting(true);
    try {
      await cxiReportGrants.upsert(orgId, String(selectedUserId), selectedPracticeAreaIds);
      await onSaved();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save grant";
      onToast(message, "error");
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  }

  const title = isEditing ? `Edit CXI grant — ${existingGrant.userEmail}` : "Add CXI report grant";

  return (
    <Modal isOpen onClose={onClose} title={title} size="small">
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
        <SearchableSelect
          name="cxi-user"
          label="User"
          required
          value={selectedUserId}
          options={userOptions}
          onChange={setSelectedUserId}
          placeholder="Select a member…"
          disabled={isEditing || submitting}
        />
        <SearchableMultiSelect
          name="cxi-practice-areas"
          label="Practice Areas"
          required
          value={selectedPracticeAreaIds}
          options={practiceAreaOptions}
          onChange={setSelectedPracticeAreaIds}
          placeholder="Select practice areas…"
          disabled={submitting}
        />

        {formError && (
          <p style={{ color: "#dc2626", fontSize: "var(--font-size-sm)", margin: 0 }}>
            {formError}
          </p>
        )}

        <div className={styles.formActions}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSave()}
            disabled={submitting || !selectedUserId || selectedPracticeAreaIds.length === 0}
          >
            {submitting ? "Saving…" : isEditing ? "Save changes" : "Add grant"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function CxiReportSection({ orgId }: CxiReportSectionProps) {
  const [grants, setGrants] = useState<CxiReportGrant[]>([]);
  const [options, setOptions] = useState<CxiGrantOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [editingGrant, setEditingGrant] = useState<CxiReportGrant | null | "new">(null);

  const existingUserIds = useMemo(() => new Set(grants.map(grant => grant.userId)), [grants]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [fetchedGrants, fetchedOptions] = await Promise.all([
        cxiReportGrants.getAll(orgId),
        cxiReportGrants.getOptions(orgId),
      ]);
      setGrants(fetchedGrants);
      setOptions(fetchedOptions);
    } catch (err) {
      setToast({
        message: `Failed to load CXI grants: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  async function handleDelete(grant: CxiReportGrant) {
    try {
      await cxiReportGrants.delete(orgId, grant.userId);
      await loadData();
      setToast({ message: `CXI grant removed for ${grant.userEmail}`, type: "success" });
    } catch (err) {
      setToast({
        message: `Failed to remove grant: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    }
  }

  async function handleSaved() {
    await loadData();
    setToast({ message: "CXI grant saved", type: "success" });
  }

  const practiceAreaNamesForGrant = useCallback(
    (grant: CxiReportGrant): string => {
      if (!options) return grant.practiceAreaIds.join(", ");
      const paMap = new Map(options.practiceAreas.map(pa => [pa.id, pa]));
      const labels = grant.practiceAreaIds.map(id => {
        const pa = paMap.get(id);
        return pa
          ? pa.country
            ? `${pa.name} (${pa.country}) · #${pa.id}`
            : `${pa.name} · #${pa.id}`
          : `#${id}`;
      });
      return labels.length > 0 ? labels.join(", ") : "—";
    },
    [options],
  );

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
          CXI Reports
        </h2>
        <PermissionGate permission="user.write_accounts">
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={() => setEditingGrant("new")}
          >
            Add grant
          </Button>
        </PermissionGate>
      </div>

      {loading ? (
        <LoadingState />
      ) : grants.length === 0 ? (
        <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
          No CXI report grants for this organisation.
        </p>
      ) : (
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}
        >
          <thead>
            <tr>
              {["User", "Practice Areas", ""].map(header => (
                <th
                  key={header}
                  style={{
                    textAlign: "left",
                    padding: "0.5rem 0.75rem",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--color-text-secondary)",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grants.map(grant => (
              <tr key={grant.userId}>
                <td
                  style={{
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {grant.userEmail}
                </td>
                <td
                  style={{
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {practiceAreaNamesForGrant(grant)}
                </td>
                <td
                  style={{
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid var(--color-border)",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  <PermissionGate permission="user.write_accounts">
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        color="default"
                        size="small"
                        onClick={() => setEditingGrant(grant)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => void handleDelete(grant)}
                      >
                        Remove
                      </Button>
                    </div>
                  </PermissionGate>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editingGrant !== null && options !== null && (
        <EditGrantModal
          orgId={orgId}
          existingGrant={editingGrant === "new" ? null : editingGrant}
          users={options.users}
          practiceAreas={options.practiceAreas}
          existingUserIds={existingUserIds}
          onSaved={handleSaved}
          onClose={() => setEditingGrant(null)}
          onToast={(message, type) => setToast({ message, type })}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}
